import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Map from '../components/Map.jsx';
import api from '../api.js';

function gameLngLat(g) {
  // Try several common field shapes from the backend.
  if (typeof g.lng === 'number' && typeof g.lat === 'number') return [g.lng, g.lat];
  if (typeof g.longitude === 'number' && typeof g.latitude === 'number')
    return [g.longitude, g.latitude];
  if (g.location?.lng != null && g.location?.lat != null)
    return [g.location.lng, g.location.lat];
  if (Array.isArray(g.location?.coordinates) && g.location.coordinates.length === 2)
    return g.location.coordinates;
  return null;
}

export default function Home() {
  const navigate = useNavigate();
  const [liveGames, setLiveGames] = useState([]);
  const [stories, setStories] = useState([]);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    const loadLive = () =>
      api.getLiveGames().then((r) => setLiveGames(r?.games || r || [])).catch(() => {});
    loadLive();
    const id = setInterval(loadLive, 30000);
    api.getStoriesFeed().then((r) => setStories(r?.stories || r || [])).catch(() => {});
    api.getCourses().then((r) => setCourses((r?.courses || r || []).slice(0, 3))).catch(() => {});
    return () => clearInterval(id);
  }, []);

  const markers = useMemo(
    () =>
      liveGames
        .map((g) => {
          const lngLat = gameLngLat(g);
          return lngLat ? { lngLat, code: g.code } : null;
        })
        .filter(Boolean),
    [liveGames]
  );

  return (
    <div className="screen" style={{ paddingTop: 0 }}>
      <div className="home-map">
        <Map markers={markers} />
      </div>

      <div className="home-hero">
        <img
          src="/icons/logo-mark.png"
          alt="Pub Golf"
          className="brand-logo brand-logo-sm home-brand"
        />
        <div className="tagline">ANY CITY · ANY NIGHT · YOUR COURSE</div>
      </div>

      {/* Stories row */}
      <div className="stories-row hide-scroll">
        <Link to="/stories/new" className="story-circle create">
          <div className="ring">
            <div>+</div>
          </div>
          <span className="name">Post</span>
        </Link>
        {stories.slice(0, 8).map((s, i) => (
          <Link to="/stories" key={s.id || i} className="story-circle">
            <div className="ring">
              <div>{s.avatar || s.emoji || '🍻'}</div>
            </div>
            <span className="name">{s.displayName || s.name || 'Player'}</span>
          </Link>
        ))}
      </div>

      {/* Main hero + join card — 2 columns on desktop */}
      <div className="home-cta-grid">
        <Link to="/create" className="feed-card feed-card-hero">
          <div className="grow">
            <h3>Tee Off Tonight</h3>
            <div className="sub">Pick a city, we'll plot the bars and the route</div>
          </div>
          <div className="round-arrow">→</div>
        </Link>

        <Link to="/join" className="feed-card">
          <div className="feed-card-icon">⌗</div>
          <div className="grow">
            <h4>Join a Round</h4>
            <div className="sub">Got a code from your group? Drop it here.</div>
          </div>
          <div style={{ color: 'var(--text20)' }}>›</div>
        </Link>
      </div>

      {/* Quick tiles */}
      <div className="quick-grid">
        <button className="quick-tile" onClick={() => navigate('/rules')}>
          <span className="icon">📖</span>
          <span>Rules</span>
        </button>
        <button className="quick-tile" onClick={() => navigate('/games')}>
          <span className="icon">🗂</span>
          <span>History</span>
        </button>
        <button className="quick-tile" onClick={() => navigate('/premium')}>
          <span className="icon" style={{ color: 'var(--warn)' }}>👑</span>
          <span>Premium</span>
        </button>
      </div>

      {/* Trending courses */}
      <div className="u-label" style={{ marginBottom: '0.75rem' }}>
        Trending This Week
      </div>
      <div className="trending-grid">
        {courses.length === 0 && (
          <div className="course-card">
            <div className="thumb">🗺️</div>
            <div className="grow">
              <h4>Broadway Honky Tonk Tour</h4>
              <div className="meta">Nashville · 9 holes · 0.3 mi</div>
              <div className="rating">
                <span>★</span>
                <span>4.9</span>
                <span className="text-muted">89 plays</span>
              </div>
            </div>
          </div>
        )}
        {courses.map((c, i) => {
          const thumb =
            c.coverPhoto ||
            c.coverImage ||
            c.photoUrl ||
            c.photo ||
            c.imageUrl ||
            c.image ||
            c.thumbnail ||
            null;
          return (
            <div className="course-card" key={c.id || i}>
              <div className="thumb">
                {thumb ? (
                  <img src={thumb} alt={c.name || 'Course'} />
                ) : (
                  c.emoji || '🗺️'
                )}
              </div>
              <div className="grow">
                <h4>{c.name || 'Untitled Course'}</h4>
                <div className="meta">
                  {c.city || 'Unknown'} · {c.holes || 9} holes
                </div>
                <div className="rating">
                  <span>★</span>
                  <span>{c.rating || '4.5'}</span>
                  <span className="text-muted">{c.plays || 0} plays</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {liveGames.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <div className="u-label" style={{ marginBottom: '0.75rem' }}>
            Live Games
          </div>
          <div className="stack-sm">
            {liveGames.slice(0, 3).map((g) => (
              <div className="feed-card" key={g.code}>
                <div className="feed-card-icon" style={{ color: 'var(--success)' }}>
                  ●
                </div>
                <div className="grow">
                  <h4>{g.city || 'Game'} · {g.holes || 9} holes</h4>
                  <div className="sub">Code {g.code}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
